"use client";

import { useEffect } from "react";
import { X, AlertCircle, CheckCircle, Info } from "lucide-react";
import { cn } from "@/lib/utils";

export type ToastVariant = "error" | "success" | "info";

export type ToastProps = {
  variant: ToastVariant;
  message: string;
  onDismiss: () => void;
  autoDismiss?: boolean;
};

export function Toast({ variant, message, onDismiss, autoDismiss = true }: ToastProps) {
  useEffect(() => {
    if (!autoDismiss || variant === "error") return;
    const t = setTimeout(onDismiss, 5000);
    return () => clearTimeout(t);
  }, [autoDismiss, variant, onDismiss]);

  const Icon = variant === "error" ? AlertCircle : variant === "success" ? CheckCircle : Info;
  const accent =
    variant === "error"
      ? "var(--status-error)"
      : variant === "success"
      ? "var(--status-complete)"
      : "var(--status-info)";

  return (
    <div
      role={variant === "error" ? "alert" : "status"}
      className={cn(
        "fixed top-3 right-3 z-50 flex max-w-[320px] items-start gap-2 rounded-lg border-l-[3px] px-3 py-2",
        "bg-[var(--surface-overlay)] shadow-[var(--shadow-overlay)]",
      )}
      style={{ borderLeftColor: accent }}
    >
      <Icon size={16} strokeWidth={1.5} style={{ color: accent }} className="mt-0.5 shrink-0" />
      <p className="flex-1 text-[13px] text-[var(--text-primary)]">{message}</p>
      <button
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
      >
        <X size={14} strokeWidth={1.5} />
      </button>
    </div>
  );
}
